import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SystemSettingsDto, SystemStatusDto, UpdateSystemSettingsInput } from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';

/** `GET /api/system/status` (defined in `@ultrasnip/contracts`). */
export type { SystemStatusDto };

export const SystemKeys = {
  status: ['system', 'status'] as const,
  settings: ['system', 'settings'] as const,
};

export const systemStatusQuery = queryOptions({
  queryKey: SystemKeys.status,
  queryFn: ({ signal }) => api.get<SystemStatusDto>('/system/status', { signal }),
  refetchInterval: 10_000,
  staleTime: 5_000,
});

/** Admin only (403 for everyone else). */
export const systemSettingsQuery = queryOptions({
  queryKey: SystemKeys.settings,
  queryFn: ({ signal }) => api.get<SystemSettingsDto>('/system/settings', { signal }),
  staleTime: 30_000,
});

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSystemSettingsInput) => api.patch<SystemSettingsDto>('/system/settings', input),
    onSuccess: (settings) => {
      queryClient.setQueryData(SystemKeys.settings, settings);
      queryClient.setQueryData<SystemStatusDto>(SystemKeys.status, (status) =>
        status ? { ...status, maintenance: settings.maintenance } : status,
      );
    },
  });
}

/** Field → message from a validation error's `details` (`[{ path, message }]`). */
export function fieldErrors(details: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(details)) return out;
  for (const item of details) {
    const { path, message } = (item ?? {}) as { path?: unknown; message?: unknown };
    if (typeof path === 'string' && typeof message === 'string' && !(path in out)) out[path] = message;
  }
  return out;
}
